// ---------------------------------------------------------------------------
// BUSCA EM VÁRIAS CIDADES — só no modo multi-filiais.
//
// O select de cidade (react-select) da home e da listagem vira `isMulti`
// EXATAMENTE no mesmo formato que o select de BAIRRO já usa há tempos
// (closeMenuOnSelect=false, hideSelectedOptions=false, contador no placeholder):
// zero CSS novo e a mesma linguagem visual do site.
//
// PORTÃO: `modoPopup` — ou `forcar` (modelos novos, Daniel 21/10: várias cidades
// em todos eles, utils/buscaMultiSelecao). Fora disso `habilitado` é false e
// `selectProps` é um objeto VAZIO — o spread no <Select> não muda nada, então o
// tenant comum continua com o select simples de uma cidade só, byte a byte.
//
// PONTE COM O CÓDIGO ANTIGO: quando exatamente UMA cidade fica marcada, o hook
// chama `onChangeCity(opcao)`; com 0 ou 2+, chama `onChangeCity(null)`. Assim
// toda a lógica de cidade única que já existe (busca de bairros, slug da URL,
// auto-seleção de cidade única, cidade fixa) segue valendo sem ser reescrita —
// e o select de bairro cai sozinho no estado "Selecione uma cidade" quando há
// várias marcadas, que é o comportamento mais simples que não quebra o fluxo.
// ---------------------------------------------------------------------------
import { useCallback, useMemo, useState } from 'react';
import { useRegiao } from '../../context/Info';
import { formatCidadesQuery, parseCidadesQuery } from '../../../lib/region';

export type OpcaoCidade = { label?: string; value?: any };

export type CidadesMulti = {
    /** modo multi-filiais ligado (portão). */
    habilitado: boolean;
    /** opções marcadas (objetos do react-select). */
    cidades: OpcaoCidade[];
    /** ids marcados, saneados. */
    ids: number[];
    /** 2+ cidades marcadas — o caso que muda a URL e usa `id_cidades`. */
    multiAtivo: boolean;
    /** "6324,6319" (só com 2+), pronto pra `?cidades=`. */
    queryCidades: string;
    setCidades: (_opcoes: OpcaoCidade[]) => void;
    /** semeia a seleção a partir de ids (ex.: `?cidades=` na carga da página). */
    semearPorIds: (_ids: number[], _opcoes: OpcaoCidade[]) => void;
    /** props extras do <Select> — `{}` quando o portão está fechado. */
    selectProps: Record<string, any>;
};

export function useCidadesMulti(
    onChangeCity: (_valor: any) => void,
    opcoes?: OpcaoCidade[] | null,
    forcar = false
): CidadesMulti {
    const { modoPopup } = useRegiao();
    const habilitado = modoPopup || forcar;
    const [cidades, setCidadesState] = useState<OpcaoCidade[]>([]);

    const ids = useMemo(
        () => parseCidadesQuery(cidades.map((c) => c?.value).join(',')),
        [cidades]
    );
    const multiAtivo = ids.length >= 2;

    const aplicar = useCallback(
        (lista: OpcaoCidade[]) => {
            const arr = Array.isArray(lista) ? lista.filter((c) => Number(c?.value) > 0) : [];
            setCidadesState(arr);
            // ponte com a lógica de cidade única (ver cabeçalho)
            onChangeCity(arr.length === 1 ? arr[0] : null);
        },
        [onChangeCity]
    );

    const onChange = useCallback(
        (valor: any) => aplicar(Array.isArray(valor) ? valor : valor ? [valor] : []),
        [aplicar]
    );

    const semearPorIds = useCallback((idsSeed: number[], lista: OpcaoCidade[]) => {
        const desejados = parseCidadesQuery((idsSeed || []).join(','));
        if (!desejados.length) return;
        const achadas = (lista || []).filter((c) => desejados.includes(Number(c?.value)));
        if (!achadas.length) return;
        setCidadesState(achadas);
    }, []);

    const selectProps = useMemo(() => {
        if (!habilitado) return {};
        return {
            isMulti: true,
            closeMenuOnSelect: false,
            hideSelectedOptions: false,
            controlShouldRenderValue: false,
            tabSelectsValue: false,
            // sem nada marcado = TODAS as cidades da filial ativa
            placeholder: cidades.length ? `${cidades.length} cidade(s)` : 'Todas as cidades',
            value: cidades,
            onChange,
            options: opcoes || undefined,
        };
    }, [habilitado, cidades, onChange, opcoes]);

    return {
        habilitado,
        cidades,
        ids,
        multiAtivo,
        queryCidades: multiAtivo ? formatCidadesQuery(ids) : '',
        setCidades: aplicar,
        semearPorIds,
        selectProps,
    };
}
